import { useState, useEffect, useCallback, useRef } from "react";
import "./KeyGen.css";
import axios from "axios";
import FormTermInput from "./Components/FormTermInput/FormTermInput";
import TermSourceList from "./Components/TermSourceList/TermSourceList";
import FrequencyTermList from "./Components/FrequencyTermList/FrequencyTermList";
import { bannedWords, fillerWords } from "../../../../../../data/bannedWords";

const sourceNameDict = {
  amazonSuggestedSearch: {
    abbreviation: "AM-SS",
    text: "Amazon Suggested Search",
  },
  amazonAdPhrases: { abbreviation: "AM-AP", text: "Amazon Ad Phrases" },
  viralLaunchKeywordResearch: {
    abbreviation: "VL-KR",
    text: "Viral Launch Keyword Research",
  },
  viralLunchCompetitorAnalysis: {
    abbreviation: "VL-CA",
    text: "Viral Launch Competitor Analysis",
  },
  amazonAnalytics: {
    abbreviation: "AM-A-HT",
    text: "Amazon Analytics(HeadTerm)",
  },
  amazonAnalyticsComp: {
    abbreviation: "AM-A-C",
    text: "Amazon Analytics(Competitors)",
  },
  jungleScout: {
    abbreviation: "JS",
    text: "Jungle Scout",
  },
  titles: {
    abbreviation: "TITLES",
    text: "Titles",
  },
  headTerm: { abbreviation: "HT", text: "Head Term & Phrase" },
};

const KeyGen = (props) => {
  const [termInput, setTermInput] = useState({
    skuOrAsinInput: "",
    headTerm: "",
    amazonSuggestedSearch: "", //Top 20 hand picked terms
    amazonAdPhrases: "",
    viralLaunchKeywordResearch: "",
    viralLunchCompetitorAnalysis: "",
    amazonAnalytics: "",
    amazonAnalyticsComp: "",
    jungleScout: "",
    titles: "",
  });
  const [finalTermState, setFinalTermState] = useState({
    amazonSuggestedSearch: null,
    secondaries: null, //Top 20 hand picked terms
    //Final Selection char count
    //   const [charCount, setCharCount] = useState(0);

    finalTermsFull: null, //Just need to alter this object to change other final state
  });
  const [showPreview, setShowPreview] = useState(false);
  const termFormRef = useRef();
  const [skuInputStatus, setSkuInputStatus] = useState(null);
  const [isValidSku, setIsValidSku] = useState(false);
  const [skuTermDict, setSkuTermDict] = useState(null);
  const parentChildSkuAssociationRef = useRef();

  const [termSourceList, setTermSourceList] = useState(null);

  const [frequencyBlocks, setFrequencyBlocks] = useState({});

  const resultsRef = useRef();

  const {
    skuOrAsinInput,
    headTerm,
    amazonSuggestedSearch, //Top 20 hand picked terms
    amazonAdPhrases,
    viralLaunchKeywordResearch,
    viralLunchCompetitorAnalysis,
    amazonAnalytics,
    amazonAnalyticsComp,
    jungleScout,
    titles,
  } = termInput;

  //Validate Sku input debounce to allow time to type without searched on every character
  //When the sku validates or not it shows the corresponding valid/invalid icon
  useEffect(() => {
    if (skuOrAsinInput && skuTermDict[skuOrAsinInput]) {
      setIsValidSku(() => true);
      setSkuInputStatus(() => "valid");
    } else if (skuOrAsinInput && !skuTermDict[skuOrAsinInput]) {
      setIsValidSku(() => false);
      setSkuInputStatus(() => "invalid");
    }
  }, [skuOrAsinInput, skuTermDict]);

  //Timer to remove sku input status icon after 3 seconds.
  //Every time sku changed the timer is reset.
  useEffect(() => {
    let removeStatusDebouce;
    if (skuInputStatus) {
      removeStatusDebouce = setTimeout(() => {
        setSkuInputStatus(null);
      }, 3000);
    }
    return () => {
      if (removeStatusDebouce) {
        clearTimeout(removeStatusDebouce);
      }
    };
  }, [skuInputStatus]);

  //Get list of all the skus for inital input
  useEffect(() => {
    axios
      .get("/sku/getSkuTermAssociation")
      .then(({ data }) => {
        parentChildSkuAssociationRef.current = data.parentChildAssociationDict;
        setSkuTermDict(() => data.skuTermDict);
        // setSkuList(() => data.skuList)
      })
      .catch((err) => console.log(err));
  }, []);

  const handleInputChange = (e) => {
    //lowercase the head term and phrase, but not sku selection
    const value =
      e.target.name === "skuOrAsinInput"
        ? e.target.value
        : e.target.value.toLowerCase();

    setTermInput((oldState) => ({
      ...oldState,
      [e.target.name]: value,
    }));
  };

  const sortTermArray = (arr) => {
    return arr.sort(({ term: a }, { term: b }) => a.localeCompare(b));
  };

  const processRawTermText = async () => {
    try {
      //emptySearchBox dictionary to accumulate terms
      let searchBoxTermList = {};

      const combinedTerms = [
        { headTerm },
        { amazonSuggestedSearch },
        { amazonAdPhrases },
        { amazonAnalytics },
        { viralLaunchKeywordResearch },
        { viralLunchCompetitorAnalysis },
        { amazonAnalyticsComp },
        { jungleScout },
        { titles },
      ].reduce((acc, list) => {
        for (let sourceName in list) {
          let tempTermHolder = list[sourceName];

          tempTermHolder = tempTermHolder.replace(/\n/g, " ");

          tempTermHolder = tempTermHolder.split(" ").reduce((dict, term) => {
            //Remove all special characters and hyphens at beginning or ending of the words
            let trimmedTerm = term.replaceAll(/[^a-z0-9]/gi, " ");

            //Trim any remaining white space and lowercase the word
            trimmedTerm = trimmedTerm.trim().toLowerCase();

            for (let subTerm of trimmedTerm.split(" ")) {
              //Counting terms and keeping original term
              if (subTerm) {
                if (!dict[subTerm]) {
                  dict[subTerm] = {
                    count: 1,
                    original: term,
                  };
                } else {
                  dict[subTerm].count++;
                }
              }
            }
            return dict;
          }, {});

          for (let term in tempTermHolder) {
            if (sourceName === "amazonSuggestedSearch") {
              searchBoxTermList[term] = true;
            }
            if (!acc[term]) {
              const banned =
                bannedWords[term] || fillerWords[term] ? true : false;
              acc[term] = {
                original: tempTermHolder[term].original,
                frequency: tempTermHolder[term].count,
                source: sourceName,
                banned,
              };
            } else {
              acc[term].frequency += tempTermHolder[term].count;
            }
          }
        }
        return acc;
      }, {});

      setFinalTermState(() => ({
        amazonSuggestedSearch: searchBoxTermList,
        finalTermsFull: combinedTerms,
      }));
    } catch (err) {
      console.log("WOAH MEGA ERROR!", err);
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    console.log(
      "Calculating bi-rotational constants and refactoring tertiary reference points to adhere to the coefficient canonical axis results in the associated percolated article table..."
    );

    //Make sure all required input fields are not empty
    const combinedTerms = [
      amazonSuggestedSearch,
      amazonAdPhrases,
      amazonAnalytics,
      amazonAnalyticsComp,
      viralLaunchKeywordResearch,
      viralLunchCompetitorAnalysis,
      jungleScout,
      titles,
    ].every((list) => list !== "");

    //If all required field are populated, process the terms
    if (combinedTerms) {
      processRawTermText();
    } else {
      alert("NO EMPTY TERM LISTS!");
    }
  };

  const generateFinalTermArray = useCallback(async () => {
    const { finalTermsFull } = finalTermState;

    //Create list for amazonSuggestedSearch terms so they can be displayed separately
    const searchBoxSuggestionArr = [];
    const headTermArr = [];

    let freqLimit3 = [];
    let freqLimit4 = [];
    let freqLimit5 = [];
    let freqLimit6 = [];
    let freqLimit7 = [];
    let freqLimit8 = [];
    let freqLimit9 = [];

    //Create array for remaining terms that are not amazonSuggestedSearch
    Object.entries(finalTermsFull).forEach(
      ([key, { frequency, source, banned, original }]) => {
        if (source === "headTerm") {
          headTermArr.push({ term: key, frequency, source, banned, original });
        } else if (source === "amazonSuggestedSearch") {
          searchBoxSuggestionArr.push({
            term: key,
            frequency,
            source,
            banned,
            original,
          });
        } else {
          if (frequency >= 3) {
            freqLimit3.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 4) {
            freqLimit4.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 5) {
            freqLimit5.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 6) {
            freqLimit6.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 7) {
            freqLimit7.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 8) {
            freqLimit8.push({ term: key, frequency, source, banned, original });
          }
          if (frequency >= 9) {
            freqLimit9.push({ term: key, frequency, source, banned, original });
          }
        }
      }
    );

    console.log({ finalTermsFull });

    console.log({ headTerm: termInput.headTerm, headTermArr });

    const orderedHeadTerms = termInput.headTerm.split(" ").map((term) => {
      return {
        term,
        frequency: 1,
        banned: bannedWords[term],
      };
    });

    freqLimit3 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit3]),
    ];
    freqLimit4 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit4]),
    ];
    freqLimit5 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit5]),
    ];
    freqLimit6 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit6]),
    ];
    freqLimit7 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit7]),
    ];
    freqLimit8 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit8]),
    ];
    freqLimit9 = [
      ...orderedHeadTerms,
      ...sortTermArray([...searchBoxSuggestionArr, ...freqLimit9]),
    ];

    setFrequencyBlocks({
      freqLimit3,
      freqLimit4,
      freqLimit5,
      freqLimit6,
      freqLimit7,
      freqLimit8,
      freqLimit9,
    });
    setShowPreview(true);
  }, [finalTermState, termInput.headTerm]);

  //Scroll to results when the are generated
  useEffect(() => {
    if (showPreview) {
      resultsRef.current.scrollIntoView();
    }
  }, [showPreview]);

  //Regenerate list any time user re-submit input terms
  useEffect(() => {
    const { amazonSuggestedSearch, finalTermsFull } = finalTermState;
    //Check to see if values are in both the amazonSuggestedSearch list and the full list
    if (amazonSuggestedSearch && finalTermsFull) {
      generateFinalTermArray();
    }
  }, [generateFinalTermArray, finalTermState]);

  return (
    <main id="keygen">
      <h2 className="keygen-section-header">KEYWORDS GENERATOR</h2>
      <FormTermInput
        handleFormSubmit={handleFormSubmit}
        termFormRef={termFormRef}
        skuOrAsinInput={skuOrAsinInput}
        skuInputStatus={skuInputStatus}
        skuTermDict={skuTermDict}
        handleInputChange={handleInputChange}
        headTerm={headTerm}
        termInput={termInput}
        isValidSku={isValidSku}
        parentChildSkuAssociationRef={parentChildSkuAssociationRef}
      />
      {showPreview ? (
        <>
          <section id="keygen-results-container" ref={resultsRef}>
            <div
              style={{ display: "flex", flexDirection: "column", gap: "2rem" }}
            >
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit9}
                frequency={9}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit8}
                frequency={8}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit7}
                frequency={7}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit6}
                frequency={6}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit5}
                frequency={5}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit4}
                frequency={4}
              />
              <FrequencyTermList
                termList={frequencyBlocks.freqLimit3}
                frequency={3}
              />
            </div>
          </section>
        </>
      ) : null}
    </main>
  );
};

export default KeyGen;
